import type { SxProps, TooltipProps } from '@mui/material';
import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import type { Theme } from '@mui/material/styles';
import {
  ATLAS_LENS_COLORS,
  ATLAS_LENS_COMPONENT_VARS,
  ATLAS_LENS_SEMANTIC_VARS,
  ATLAS_LENS_TOGGLE_VARS,
  resolveAtlasTokenString,
} from './atlasLensTokens';

/** MUI theme with Atlas Lens design tokens — use for runtime `tokens.*` access. */
export type AtlasLensTheme = Theme & LensTheme;

const SPACING_FALLBACK = {
  '0': '0px',
  '0_5': '4px',
  '1': '8px',
  '1_5': '12px',
  '2': '16px',
  '2_5': '20px',
  '3': '24px',
  '4': '32px',
  '5': '40px',
  px: '1px',
} as const;

const TYPOGRAPHY_FALLBACK = {
  body: { fontSize: '16px', lineHeight: '24px', letterSpacing: '0.2px' },
  sm: { fontSize: '12px', lineHeight: '16px', letterSpacing: '0.3px' },
  md: { fontSize: '14px', lineHeight: '20px', letterSpacing: '0.2px' },
  h3Lg: { fontSize: '22px', lineHeight: '28px' },
  h1Billboard: { fontSize: '30px', lineHeight: '38px', letterSpacing: '0' },
} as const;

function tokensOf(theme: AtlasLensTheme) {
  return theme.tokens;
}

export function atlasSpacing(
  theme: AtlasLensTheme,
  size: keyof typeof SPACING_FALLBACK,
): string {
  return tokensOf(theme)?.core?.spacing?.[size]?.value ?? SPACING_FALLBACK[size];
}

export function atlasRadiusLg(theme: AtlasLensTheme): string {
  return tokensOf(theme)?.semantic?.radius?.lg?.value ?? '12px';
}

export function atlasRadiusMd(theme: AtlasLensTheme): string {
  return tokensOf(theme)?.semantic?.radius?.md?.value ?? '8px';
}

/** Figma 160px — core.spacing-8×2 + spacing-4 (course card thumbnail). */
export function atlasCourseCardMediaHeight(theme: AtlasLensTheme): string {
  const spacing = tokensOf(theme)?.core?.spacing;
  const eight = parseFloat(spacing?.['8']?.value ?? '64');
  const four = parseFloat(spacing?.['4']?.value ?? '32');
  return `${eight * 2 + four}px`;
}

export function atlasLayoutXlMaxWidth(theme: AtlasLensTheme): string {
  const layout = tokensOf(theme)?.semantic?.layout as
    | { xl?: { maxWidth?: { value: string } } }
    | undefined;
  return layout?.xl?.maxWidth?.value ?? ATLAS_LENS_SEMANTIC_VARS.layoutXlMaxWidth;
}

export function atlasFontWeightSemiBold(theme: AtlasLensTheme): number | string {
  return tokensOf(theme)?.core?.fontWeight?.semiBold?.value ?? 600;
}

/** Modal footer / section divider — component.modal.footer.borderTopWidth + semantic divider color. */
export function atlasBorderTopDivider(
  theme: AtlasLensTheme,
  dividerColor: string = ATLAS_LENS_SEMANTIC_VARS.dividerDefault,
): string {
  const footer = tokensOf(theme)?.component?.modal as
    | { footer?: { borderTopWidth?: { value: string } } }
    | undefined;
  const width =
    footer?.footer?.borderTopWidth?.value ??
    tokensOf(theme)?.core?.spacing?.px?.value ??
    SPACING_FALLBACK.px;
  return `${width} solid ${dividerColor}`;
}

export function atlasSpinnerSizeMd(theme: AtlasLensTheme): number {
  return parseFloat(atlasSpacing(theme, '2_5'));
}

export function atlasSpinnerSizeSm(theme: AtlasLensTheme): number {
  return parseFloat(atlasSpacing(theme, '2'));
}

export function atlasButtonMediumHeight(theme: AtlasLensTheme): string {
  const button = tokensOf(theme)?.component?.button as
    | { medium?: { height?: { value: string } } }
    | undefined;
  return button?.medium?.height?.value ?? ATLAS_LENS_COMPONENT_VARS.buttonMediumHeight;
}

export function atlasButtonMediumBorderRadius(theme: AtlasLensTheme): string {
  const button = tokensOf(theme)?.component?.button as
    | { medium?: { borderRadius?: { value: string } } }
    | undefined;
  return button?.medium?.borderRadius?.value ?? ATLAS_LENS_COMPONENT_VARS.buttonMediumBorderRadius;
}

export function atlasModalShadow(theme: AtlasLensTheme): string {
  return resolveAtlasTokenString(
    tokensOf(theme)?.component?.modal?.boxShadow?.value,
    ATLAS_LENS_COMPONENT_VARS.modalBoxShadow,
  );
}

export function atlasInputMediumHeight(theme: AtlasLensTheme): string {
  return (
    tokensOf(theme)?.component?.input?.medium?.container?.height?.value ??
    ATLAS_LENS_COMPONENT_VARS.inputMediumHeight
  );
}

export function atlasFontTextBody(theme: AtlasLensTheme) {
  const body = tokensOf(theme)?.semantic?.font?.text?.body;
  return {
    fontSize: body?.fontSize?.value ?? TYPOGRAPHY_FALLBACK.body.fontSize,
    lineHeight: body?.lineHeight?.value ?? TYPOGRAPHY_FALLBACK.body.lineHeight,
    letterSpacing: body?.letterSpacing?.value ?? TYPOGRAPHY_FALLBACK.body.letterSpacing,
    fontWeight: tokensOf(theme)?.core?.fontWeight?.regular?.value ?? 400,
  };
}

/** Body emphasis — Semantic/Font/Text/Body - Emphasis (Figma asset summary filename). */
export function atlasFontTextBodyEmphasis(theme: AtlasLensTheme) {
  const body = tokensOf(theme)?.semantic?.font?.text?.body;
  return {
    fontSize: body?.fontSize?.value ?? TYPOGRAPHY_FALLBACK.body.fontSize,
    lineHeight: body?.lineHeight?.value ?? TYPOGRAPHY_FALLBACK.body.lineHeight,
    letterSpacing: body?.letterSpacing?.value ?? TYPOGRAPHY_FALLBACK.body.letterSpacing,
    fontWeight: tokensOf(theme)?.core?.fontWeight?.semiBold?.value ?? 600,
  };
}

export function atlasFontTextSm(theme: AtlasLensTheme) {
  const sm = tokensOf(theme)?.semantic?.font?.text?.sm;
  return {
    fontSize: sm?.fontSize?.value ?? TYPOGRAPHY_FALLBACK.sm.fontSize,
    lineHeight: sm?.lineHeight?.value ?? TYPOGRAPHY_FALLBACK.sm.lineHeight,
    letterSpacing: sm?.letterSpacing?.value ?? TYPOGRAPHY_FALLBACK.sm.letterSpacing,
    fontWeight: tokensOf(theme)?.core?.fontWeight?.regular?.value ?? 400,
  };
}

export function atlasFontTextMd(theme: AtlasLensTheme) {
  const md = tokensOf(theme)?.semantic?.font?.text?.md;
  return {
    fontSize: md?.fontSize?.value ?? TYPOGRAPHY_FALLBACK.md.fontSize,
    lineHeight: md?.lineHeight?.value ?? TYPOGRAPHY_FALLBACK.md.lineHeight,
    letterSpacing: md?.letterSpacing?.value ?? TYPOGRAPHY_FALLBACK.md.letterSpacing,
    fontWeight: tokensOf(theme)?.core?.fontWeight?.regular?.value ?? 400,
  };
}

export function atlasFontTitleH3Lg(theme: AtlasLensTheme) {
  const title = tokensOf(theme)?.semantic?.font?.title?.h3Lg;
  return {
    fontSize: title?.fontSize?.value ?? TYPOGRAPHY_FALLBACK.h3Lg.fontSize,
    lineHeight: title?.lineHeight?.value ?? TYPOGRAPHY_FALLBACK.h3Lg.lineHeight,
    fontWeight: tokensOf(theme)?.core?.fontWeight?.semiBold?.value ?? 600,
  };
}

/** Presales page title — Semantic/Font/Title/H1 Billboard. */
export function atlasFontTitleH1Billboard(theme: AtlasLensTheme) {
  const title = tokensOf(theme)?.semantic?.font?.title?.h1Billboard;
  return {
    fontSize: title?.fontSize?.value ?? TYPOGRAPHY_FALLBACK.h1Billboard.fontSize,
    lineHeight: title?.lineHeight?.value ?? TYPOGRAPHY_FALLBACK.h1Billboard.lineHeight,
    fontWeight: tokensOf(theme)?.core?.fontWeight?.semiBold?.value ?? 600,
    letterSpacing: title?.letterSpacing?.value ?? TYPOGRAPHY_FALLBACK.h1Billboard.letterSpacing,
  };
}

/** Form field label — Semantic/Font/Label/Sm - Emphasis (Figma 16113:67774). */
export function atlasFontLabelSmEmphasis(theme: AtlasLensTheme) {
  const label = tokensOf(theme)?.semantic?.font?.label as
    | {
        sm?: {
          fontSize?: { value: string };
          lineHeight?: { value: string };
          letterSpacing?: { value: string };
        };
      }
    | undefined;
  const sm = label?.sm;
  return {
    fontSize: sm?.fontSize?.value ?? TYPOGRAPHY_FALLBACK.sm.fontSize,
    lineHeight: sm?.lineHeight?.value ?? TYPOGRAPHY_FALLBACK.sm.lineHeight,
    letterSpacing: sm?.letterSpacing?.value ?? TYPOGRAPHY_FALLBACK.sm.letterSpacing,
    fontWeight: tokensOf(theme)?.core?.fontWeight?.semiBold?.value ?? 600,
  };
}

/** Muted rail meta — Semantic/Font/Label/Sm (regular weight). */
export function atlasFontLabelSm(theme: AtlasLensTheme) {
  const label = tokensOf(theme)?.semantic?.font?.label as
    | {
        sm?: {
          fontSize?: { value: string };
          lineHeight?: { value: string };
          letterSpacing?: { value: string };
        };
      }
    | undefined;
  const sm = label?.sm;
  return {
    fontSize: sm?.fontSize?.value ?? TYPOGRAPHY_FALLBACK.sm.fontSize,
    lineHeight: sm?.lineHeight?.value ?? TYPOGRAPHY_FALLBACK.sm.lineHeight,
    letterSpacing: sm?.letterSpacing?.value ?? TYPOGRAPHY_FALLBACK.sm.letterSpacing,
    fontWeight: tokensOf(theme)?.core?.fontWeight?.regular?.value ?? 400,
  };
}

/** Drop zone heading — Semantic/Font/Title/H5 Sm - Emphasis (Figma 16113:65304). */
export function atlasFontTitleH5SmEmphasis(theme: AtlasLensTheme) {
  const title = tokensOf(theme)?.semantic?.font?.title?.h5Sm;
  return {
    fontSize: title?.fontSize?.value ?? TYPOGRAPHY_FALLBACK.body.fontSize,
    lineHeight: title?.lineHeight?.value ?? TYPOGRAPHY_FALLBACK.body.lineHeight,
    fontWeight: tokensOf(theme)?.core?.fontWeight?.semiBold?.value ?? 600,
    letterSpacing: title?.letterSpacing?.value ?? TYPOGRAPHY_FALLBACK.body.letterSpacing,
  };
}

/** Page header subtitle — Semantic/Font/Title/H5 Sm (Figma 16076:62216). */
export function atlasFontTitleH5Sm(theme: AtlasLensTheme) {
  const title = tokensOf(theme)?.semantic?.font?.title?.h5Sm;
  return {
    fontSize: title?.fontSize?.value ?? TYPOGRAPHY_FALLBACK.body.fontSize,
    lineHeight: title?.lineHeight?.value ?? TYPOGRAPHY_FALLBACK.body.lineHeight,
    fontWeight: tokensOf(theme)?.core?.fontWeight?.regular?.value ?? 400,
    letterSpacing: title?.letterSpacing?.value ?? TYPOGRAPHY_FALLBACK.body.letterSpacing,
  };
}

/** Section header title — Semantic/Font/Title/H4 Md - Emphasis (Figma 16088:64237). */
export function atlasFontTitleH4Md(theme: AtlasLensTheme) {
  const title = tokensOf(theme)?.semantic?.font?.title?.h4Md;
  return {
    fontSize: title?.fontSize?.value ?? '18px',
    lineHeight: title?.lineHeight?.value ?? '28px',
    fontWeight: tokensOf(theme)?.core?.fontWeight?.semiBold?.value ?? 600,
    letterSpacing: title?.letterSpacing?.value ?? '0.2px',
  };
}

/** Section header subtitle — Semantic/Font/Title/H7 (Figma 16088:64237). */
export function atlasFontTitleH7(theme: AtlasLensTheme) {
  const title = tokensOf(theme)?.semantic?.font?.title as
    | {
        h7?: {
          fontSize?: { value: string };
          lineHeight?: { value: string };
          letterSpacing?: { value: string };
        };
      }
    | undefined;
  const h7 = title?.h7;
  return {
    fontSize: h7?.fontSize?.value ?? TYPOGRAPHY_FALLBACK.sm.fontSize,
    lineHeight: h7?.lineHeight?.value ?? TYPOGRAPHY_FALLBACK.sm.lineHeight,
    fontWeight: tokensOf(theme)?.core?.fontWeight?.regular?.value ?? 400,
    letterSpacing: h7?.letterSpacing?.value ?? TYPOGRAPHY_FALLBACK.sm.letterSpacing,
  };
}

/** Card title — Semantic/Font/Text/Md - Emphasis (Figma wizard list item). */
export function atlasFontTextMdEmphasis(theme: AtlasLensTheme) {
  const md = tokensOf(theme)?.semantic?.font?.text?.md;
  return {
    fontSize: md?.fontSize?.value ?? TYPOGRAPHY_FALLBACK.md.fontSize,
    lineHeight: md?.lineHeight?.value ?? TYPOGRAPHY_FALLBACK.md.lineHeight,
    letterSpacing: md?.letterSpacing?.value ?? TYPOGRAPHY_FALLBACK.md.letterSpacing,
    fontWeight: tokensOf(theme)?.core?.fontWeight?.semiBold?.value ?? 600,
  };
}

/** Card duration — Semantic/Font/Label/Xs (Figma wizard list item). */
export function atlasFontLabelXs(theme: AtlasLensTheme) {
  const label = tokensOf(theme)?.semantic?.font?.label?.xs;
  return {
    fontSize: label?.fontSize?.value ?? '11px',
    lineHeight: label?.lineHeight?.value ?? TYPOGRAPHY_FALLBACK.sm.lineHeight,
    letterSpacing: label?.letterSpacing?.value ?? '0.4px',
    fontWeight: tokensOf(theme)?.core?.fontWeight?.regular?.value ?? 400,
  };
}

export function atlasRadiusLg16(theme: AtlasLensTheme): string {
  const radius = tokensOf(theme)?.semantic?.radius as { lg16?: { value: string } } | undefined;
  return radius?.lg16?.value ?? '16px';
}

export function atlasFontStepLabel(theme: AtlasLensTheme, active: boolean) {
  const md = tokensOf(theme)?.semantic?.font?.text?.md;
  return {
    fontSize: md?.fontSize?.value ?? TYPOGRAPHY_FALLBACK.md.fontSize,
    lineHeight: md?.lineHeight?.value ?? TYPOGRAPHY_FALLBACK.md.lineHeight,
    letterSpacing: md?.letterSpacing?.value ?? TYPOGRAPHY_FALLBACK.md.letterSpacing,
    fontWeight: active
      ? (tokensOf(theme)?.core?.fontWeight?.semiBold?.value ?? 600)
      : (tokensOf(theme)?.core?.fontWeight?.regular?.value ?? 400),
  };
}

export function atlasFontStepBadge(theme: AtlasLensTheme) {
  const md = tokensOf(theme)?.semantic?.font?.text?.md;
  return {
    fontSize: md?.fontSize?.value ?? TYPOGRAPHY_FALLBACK.md.fontSize,
    lineHeight: md?.lineHeight?.value ?? TYPOGRAPHY_FALLBACK.md.lineHeight,
    letterSpacing: md?.letterSpacing?.value ?? TYPOGRAPHY_FALLBACK.md.letterSpacing,
    fontWeight: tokensOf(theme)?.core?.fontWeight?.semiBold?.value ?? 600,
    fontFeatureSettings: '"lnum" 1, "tnum" 1',
  };
}

/** Component/Font/Button/Lg — outlined and contained wizard actions. */
export function atlasButtonLgSx(theme: AtlasLensTheme) {
  const body = atlasFontTextBody(theme);
  return {
    textTransform: 'none' as const,
    borderRadius: atlasRadiusLg(theme),
    minHeight: atlasInputMediumHeight(theme),
    height: atlasInputMediumHeight(theme),
    fontSize: body.fontSize,
    fontWeight: tokensOf(theme)?.core?.fontWeight?.semiBold?.value ?? 600,
    lineHeight: body.lineHeight,
    letterSpacing: body.letterSpacing,
    px: atlasSpacing(theme, '1_5'),
    flexShrink: 0,
    whiteSpace: 'nowrap' as const,
  };
}

/** Component/Button/Medium — modal footer primary actions (Figma 40px hug, padding 8×12). */
export function atlasButtonMdSx(theme: AtlasLensTheme) {
  const label = atlasFontTextBody(theme);
  return {
    textTransform: 'none' as const,
    borderRadius: atlasButtonMediumBorderRadius(theme),
    minHeight: atlasButtonMediumHeight(theme),
    height: atlasButtonMediumHeight(theme),
    py: atlasSpacing(theme, '1'),
    px: atlasSpacing(theme, '1_5'),
    fontSize: label.fontSize,
    fontWeight: tokensOf(theme)?.core?.fontWeight?.semiBold?.value ?? 600,
    lineHeight: label.lineHeight,
    letterSpacing: label.letterSpacing,
    flexShrink: 0,
    whiteSpace: 'nowrap' as const,
    boxShadow: 'none',
  };
}

/** Disabled contained primary — semantic.action.primary.disabled / on-primary-disabled. */
export function atlasPrimaryDisabledButtonSx() {
  return {
    backgroundColor: ATLAS_LENS_SEMANTIC_VARS.actionPrimaryDisabled,
    color: ATLAS_LENS_SEMANTIC_VARS.actionPrimaryOnPrimaryDisabled,
    opacity: 1,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: ATLAS_LENS_SEMANTIC_VARS.actionPrimaryDisabled,
      color: ATLAS_LENS_SEMANTIC_VARS.actionPrimaryOnPrimaryDisabled,
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      backgroundColor: ATLAS_LENS_SEMANTIC_VARS.actionPrimaryDisabled,
      color: ATLAS_LENS_SEMANTIC_VARS.actionPrimaryOnPrimaryDisabled,
      opacity: 1,
    },
  };
}

/** Authorize-dialog checkbox card — Figma Card: column, padding 12×16, min-height 64. */
export function atlasAuthCheckboxCardSx(theme: AtlasLensTheme) {
  return {
    boxSizing: 'border-box' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
    width: '100%',
    minHeight: `calc(${atlasSpacing(theme, '1_5')} * 2 + ${atlasSpacing(theme, '5')})`,
    py: atlasSpacing(theme, '1_5'),
    px: atlasSpacing(theme, '2'),
    borderRadius: atlasRadiusLg(theme),
    backgroundColor: ATLAS_LENS_SEMANTIC_VARS.surfaceVariant,
  };
}

/** Figma Content row — row, align center, gap 12, min-height 40, stretch. */
export function atlasAuthCheckboxRowSx(theme: AtlasLensTheme) {
  return {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
    alignSelf: 'stretch',
    gap: atlasSpacing(theme, '1_5'),
    width: '100%',
    minHeight: atlasSpacing(theme, '5'),
    m: 0,
    p: 0,
  };
}

/** Checkbox in auth card — 24×24, centered in the 40px content row. */
export function atlasAuthCheckboxControlSx() {
  return {
    p: 0,
    m: 0,
    flexShrink: 0,
    '& .MuiSvgIcon-root': {
      width: ATLAS_LENS_COMPONENT_VARS.checkboxWidth,
      height: ATLAS_LENS_COMPONENT_VARS.checkboxHeight,
      fontSize: ATLAS_LENS_COMPONENT_VARS.checkboxWidth,
      borderRadius: ATLAS_LENS_COMPONENT_VARS.checkboxRadius,
    },
  };
}

/** component.checkbox.* — 24×24 icon; do not set root width/height (collapses MUI icon). */
export function atlasCheckboxControlSx() {
  return {
    p: 0,
    m: 0,
    flexShrink: 0,
    alignSelf: 'flex-start',
    '& .MuiSvgIcon-root': {
      width: ATLAS_LENS_COMPONENT_VARS.checkboxWidth,
      height: ATLAS_LENS_COMPONENT_VARS.checkboxHeight,
      fontSize: ATLAS_LENS_COMPONENT_VARS.checkboxWidth,
      borderRadius: ATLAS_LENS_COMPONENT_VARS.checkboxRadius,
    },
  };
}

export function atlasToastTextButtonSx(theme: AtlasLensTheme) {
  const sm = atlasFontTextSm(theme);
  return {
    textTransform: 'none' as const,
    fontWeight: tokensOf(theme)?.core?.fontWeight?.semiBold?.value ?? 600,
    fontSize: sm.fontSize,
    lineHeight: sm.lineHeight,
    minWidth: 'auto',
    px: atlasSpacing(theme, '1'),
    flexShrink: 0,
  };
}

export function atlasToastContainerSx(
  theme: AtlasLensTheme,
  width: number,
  height: number,
) {
  return {
    display: 'flex' as const,
    alignItems: 'stretch' as const,
    width,
    minWidth: 288,
    maxWidth: width,
    height,
    borderRadius: atlasRadiusLg(theme),
    overflow: 'hidden' as const,
    boxShadow: atlasModalShadow(theme),
  };
}

export function atlasToastAccentStripSx(theme: AtlasLensTheme, width: number) {
  return {
    display: 'flex' as const,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    width,
    minWidth: width,
    px: atlasSpacing(theme, '1'),
    py: atlasSpacing(theme, '2'),
    borderRadius: atlasRadiusLg(theme),
    flexShrink: 0,
  };
}

export function atlasToastBodySx(theme: AtlasLensTheme) {
  return {
    display: 'flex' as const,
    alignItems: 'center' as const,
    flex: 1,
    px: atlasSpacing(theme, '2'),
    py: atlasSpacing(theme, '1'),
    gap: atlasSpacing(theme, '1'),
  };
}

/** Atlas Lens component.toast.alert — presales error toast shell. */
export function atlasToastAlertContainerSx(theme: AtlasLensTheme) {
  const alert = tokensOf(theme)?.component?.toast?.alert;
  return {
    display: 'flex' as const,
    alignItems: 'stretch' as const,
    width: '100%',
    minWidth: alert?.minWidth?.value ?? ATLAS_LENS_COMPONENT_VARS.toastAlertMinWidth,
    maxWidth: alert?.maxWidth?.value ?? ATLAS_LENS_COMPONENT_VARS.toastAlertMaxWidth,
    minHeight: alert?.minHeight?.value ?? ATLAS_LENS_COMPONENT_VARS.toastAlertMinHeight,
    borderRadius: alert?.borderRadius?.value ?? ATLAS_LENS_COMPONENT_VARS.toastAlertBorderRadius,
    overflow: 'hidden' as const,
    boxShadow: atlasModalShadow(theme),
  };
}

/** Atlas Lens component.toast.alert.icon — colored accent strip with status icon. */
export function atlasToastAlertIconStripSx(theme: AtlasLensTheme) {
  const icon = tokensOf(theme)?.component?.toast?.alert?.icon;
  return {
    display: 'flex' as const,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    width: icon?.width?.value ?? ATLAS_LENS_COMPONENT_VARS.toastAlertIconWidth,
    minWidth: icon?.width?.value ?? ATLAS_LENS_COMPONENT_VARS.toastAlertIconWidth,
    minHeight: icon?.minHeight?.value ?? ATLAS_LENS_COMPONENT_VARS.toastAlertIconMinHeight,
    px: atlasSpacing(theme, '1'),
    py: atlasSpacing(theme, '2'),
    borderRadius: icon?.borderRadius?.value ?? ATLAS_LENS_COMPONENT_VARS.toastAlertIconBorderRadius,
    flexShrink: 0,
  };
}

export function atlasInverseGhostHoverSx(theme: AtlasLensTheme) {
  const action = tokensOf(theme)?.semantic?.color?.action as
    | { ghost?: { hover?: { value: string } } }
    | undefined;
  const hover = action?.ghost?.hover?.value ?? 'rgba(255, 255, 255, 0.08)';
  return {
    '&:hover': {
      backgroundColor: hover,
    },
  };
}

/** Atlas Lens tooltip chrome — dark surface, inverse text (Figma / LocationField pattern). */
export function atlasLensTooltipSlotProps(theme: AtlasLensTheme): TooltipProps['slotProps'] {
  const tooltipText = atlasFontTextSm(theme);
  const { tokens } = theme;
  return {
    tooltip: {
      sx: {
        bgcolor: ATLAS_LENS_COLORS.typeDefault,
        color: ATLAS_LENS_COLORS.typeInverse,
        fontSize: tooltipText.fontSize,
        lineHeight: tooltipText.lineHeight,
        letterSpacing: tooltipText.letterSpacing,
        fontWeight: tokens.core.fontWeight.regular.value,
        px: atlasSpacing(theme, '1_5'),
        py: atlasSpacing(theme, '1'),
        borderRadius: atlasRadiusMd(theme),
      },
    },
    arrow: {
      sx: {
        color: ATLAS_LENS_COLORS.typeDefault,
      },
    },
  };
}

/** Circular ghost icon button hover on default surfaces (editor header undo/redo). */
export function atlasGhostIconButtonSx(_theme: AtlasLensTheme) {
  return {
    color: ATLAS_LENS_SEMANTIC_VARS.typeDefault,
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: ATLAS_LENS_SEMANTIC_VARS.surfaceSubtle,
    },
    '&.Mui-disabled': {
      color: ATLAS_LENS_SEMANTIC_VARS.typeMuted,
    },
  };
}

/**
 * Atlas Lens toggle (48×24) — component.toggle.large mapped to MuiSwitch size="medium".
 * Figma Settings — Data and AI list item trailing switch.
 */
export function atlasToggleLargeMetrics(theme: AtlasLensTheme) {
  const large = tokensOf(theme)?.component?.toggle?.large as
    | {
        bar?: {
          width?: { value: string };
          height?: { value: string };
          padding?: { value: string };
          borderRadius?: { value: string };
        };
        knob?: {
          width?: { value: string };
          height?: { value: string };
        };
      }
    | undefined;

  return {
    barWidth: large?.bar?.width?.value ?? ATLAS_LENS_TOGGLE_VARS.largeBarWidth,
    barHeight: large?.bar?.height?.value ?? ATLAS_LENS_TOGGLE_VARS.largeBarHeight,
    barPadding: large?.bar?.padding?.value ?? ATLAS_LENS_TOGGLE_VARS.largeBarPadding,
    knobWidth: large?.knob?.width?.value ?? ATLAS_LENS_TOGGLE_VARS.largeKnobWidth,
    knobHeight: large?.knob?.height?.value ?? ATLAS_LENS_TOGGLE_VARS.largeKnobHeight,
    barBorderRadius:
      large?.bar?.borderRadius?.value ?? ATLAS_LENS_TOGGLE_VARS.largeBarBorderRadius,
  };
}

export function atlasToggleTrackColors(theme: AtlasLensTheme, checked: boolean) {
  const state = checked ? 'checked' : 'unchecked';
  const toggle = tokensOf(theme)?.component?.toggle as
    | Record<
        string,
        {
          default?: {
            bar?: {
              background?: { value: string };
              borderColor?: { value: string };
            };
            knob?: {
              backgroundColor?: { value: string };
              borderColor?: { value: string };
            };
          };
        }
      >
    | undefined;
  const palette = toggle?.[state]?.default;

  if (checked) {
    return {
      barBackground:
        palette?.bar?.background?.value ?? ATLAS_LENS_TOGGLE_VARS.checkedDefaultBarBackground,
      barBorderColor:
        palette?.bar?.borderColor?.value ?? ATLAS_LENS_TOGGLE_VARS.checkedDefaultBarBorderColor,
      knobBackground:
        palette?.knob?.backgroundColor?.value ?? ATLAS_LENS_TOGGLE_VARS.checkedDefaultKnobBackground,
      knobBorderColor:
        palette?.knob?.borderColor?.value ?? ATLAS_LENS_TOGGLE_VARS.checkedDefaultKnobBorderColor,
    };
  }

  return {
    barBackground:
      palette?.bar?.background?.value ?? ATLAS_LENS_TOGGLE_VARS.uncheckedDefaultBarBackground,
    barBorderColor:
      palette?.bar?.borderColor?.value ?? ATLAS_LENS_TOGGLE_VARS.uncheckedDefaultBarBorderColor,
    knobBackground:
      palette?.knob?.backgroundColor?.value ?? ATLAS_LENS_TOGGLE_VARS.uncheckedDefaultKnobBackground,
    knobBorderColor:
      palette?.knob?.borderColor?.value ?? ATLAS_LENS_TOGGLE_VARS.uncheckedDefaultKnobBorderColor,
  };
}

/** component.modal.* — confirm / alert dialog shell from Atlas Lens tokens. */
export function atlasLensModalShell(theme: AtlasLensTheme) {
  const modal = tokensOf(theme)?.component?.modal as
    | {
        borderRadius?: { value: string };
        backgroundColor?: { value: string };
        maxWidth?: { value: string };
        header?: {
          paddingLeft?: { value: string };
          paddingRight?: { value: string };
          paddingBottom?: { value: string };
          gap?: { value: string };
          icon?: { width?: { value: string } };
        };
        content?: {
          paddingLeft?: { value: string };
          paddingRight?: { value: string };
          paddingBottom?: { value: string };
        };
        footer?: {
          gap?: { value: string };
          paddingLeft?: { value: string };
          paddingRight?: { value: string };
          paddingTop?: { value: string };
          paddingBottom?: { value: string };
          borderTopWidth?: { value: string };
          borderTopColor?: { value: string };
        };
      }
    | undefined;

  const header = modal?.header;
  const content = modal?.content;
  const footer = modal?.footer;
  const dialogWidth = `calc(${atlasSpacing(theme, '5')} * 11)`;
  const dialogMinWidth = `calc(${atlasSpacing(theme, '5')} * 10)`;
  const dialogMaxWidth = `calc(${atlasSpacing(theme, '5')} * 12)`;

  return {
    paper: {
      width: dialogWidth,
      minWidth: dialogMinWidth,
      maxWidth: modal?.maxWidth?.value ?? dialogMaxWidth,
      borderRadius: modal?.borderRadius?.value ?? ATLAS_LENS_COMPONENT_VARS.modalBorderRadius,
      boxShadow: atlasModalShadow(theme),
      backgroundColor: modal?.backgroundColor?.value ?? ATLAS_LENS_COMPONENT_VARS.modalBackground,
      m: atlasSpacing(theme, '2'),
      overflow: 'hidden',
      pt: header?.paddingBottom?.value ?? atlasSpacing(theme, '3'),
      pb: 0,
      px: 0,
    },
    header: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: header?.gap?.value ?? atlasSpacing(theme, '1_5'),
      px: header?.paddingLeft?.value ?? atlasSpacing(theme, '3'),
      pt: 0,
      pb: header?.paddingBottom?.value ?? atlasSpacing(theme, '3'),
      m: 0,
    },
    closeButton: {
      width: atlasSpacing(theme, '4'),
      height: atlasSpacing(theme, '4'),
      p: atlasSpacing(theme, '0_5'),
      borderRadius: atlasRadiusMd(theme),
      flexShrink: 0,
      color: ATLAS_LENS_SEMANTIC_VARS.typeDefault,
    },
    closeIconSize: header?.icon?.width?.value ?? atlasSpacing(theme, '3'),
    content: {
      px: content?.paddingLeft?.value ?? atlasSpacing(theme, '3'),
      pb: content?.paddingBottom?.value ?? atlasSpacing(theme, '3'),
      py: 0,
    },
    footer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      boxSizing: 'border-box' as const,
      p: 0,
      padding: `${footer?.paddingTop?.value ?? atlasSpacing(theme, '2')} ${footer?.paddingLeft?.value ?? atlasSpacing(theme, '2')} ${footer?.paddingBottom?.value ?? atlasSpacing(theme, '2')}`,
      borderTop: atlasBorderTopDivider(
        theme,
        footer?.borderTopColor?.value ?? ATLAS_LENS_SEMANTIC_VARS.dividerDefault,
      ),
      m: 0,
      gap: footer?.gap?.value ?? atlasSpacing(theme, '1_5'),
      minHeight: `calc(${atlasSpacing(theme, '2')} * 2 + ${atlasButtonMediumHeight(theme)})`,
      '& > :not(:first-of-type)': { ml: 0 },
    },
    footerEndAligned: {
      justifyContent: 'flex-end',
    },
    dialogButton: {
      ...atlasButtonLgSx(theme),
      boxShadow: 'none',
      minWidth: 'auto',
    },
    outlinedButton: {
      color: ATLAS_LENS_COMPONENT_VARS.buttonSecondaryDefaultColor,
      borderColor: ATLAS_LENS_COMPONENT_VARS.buttonSecondaryDefaultBorder,
      backgroundColor: ATLAS_LENS_COMPONENT_VARS.buttonSecondaryDefaultBackground,
      '&:hover': {
        borderColor: ATLAS_LENS_COMPONENT_VARS.buttonSecondaryHoverBorder,
        backgroundColor: ATLAS_LENS_COMPONENT_VARS.buttonSecondaryHoverBackground,
      },
    },
    containedButton: {
      backgroundColor: ATLAS_LENS_COMPONENT_VARS.buttonPrimaryDefaultBackground,
      color: ATLAS_LENS_COMPONENT_VARS.buttonPrimaryDefaultColor,
      '&:hover': {
        backgroundColor: ATLAS_LENS_COMPONENT_VARS.buttonPrimaryHoverBackground,
        color: ATLAS_LENS_COMPONENT_VARS.buttonPrimaryHoverColor,
        boxShadow: 'none',
      },
    },
    containedButtonLoading: {
      backgroundColor: ATLAS_LENS_COMPONENT_VARS.buttonPrimaryDisabledBackground,
      color: ATLAS_LENS_COMPONENT_VARS.buttonPrimaryDisabledColor,
      opacity: 1,
      boxShadow: 'none',
      '&:hover': {
        backgroundColor: ATLAS_LENS_COMPONENT_VARS.buttonPrimaryDisabledBackground,
        boxShadow: 'none',
      },
      '&.Mui-disabled': {
        backgroundColor: ATLAS_LENS_COMPONENT_VARS.buttonPrimaryDisabledBackground,
        color: ATLAS_LENS_COMPONENT_VARS.buttonPrimaryDisabledColor,
        opacity: 1,
      },
    },
  };
}

/** Centers a nested MUI Dialog over its onboarding wizard parent (not the viewport). */
export function atlasLensNestedDialogRootSx(zIndex: number): SxProps<Theme> {
  return {
    position: 'absolute',
    zIndex,
    '& .MuiBackdrop-root': {
      position: 'absolute',
    },
    '& .MuiDialog-container': {
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  };
}

export function atlasToggleSwitchSx(theme: AtlasLensTheme): SxProps<Theme> {
  const { barWidth, barHeight, knobWidth, knobHeight, barPadding, barBorderRadius } =
    atlasToggleLargeMetrics(theme);
  const uncheckedColors = atlasToggleTrackColors(theme, false);
  const checkedColors = atlasToggleTrackColors(theme, true);

  return {
    width: barWidth,
    height: barHeight,
    minWidth: barWidth,
    p: 0,
    m: 0,
    flexShrink: 0,
    overflow: 'visible',
    // Do not stretch switchBase/input to 100% — that breaks MUI's hit target and thumb motion.
    '& .MuiSwitch-switchBase': {
      p: barPadding,
      '&.Mui-checked': {
        transform: `translateX(calc(${barWidth} - ${knobWidth} - ${barPadding} * 2))`,
        '& + .MuiSwitch-track': {
          opacity: 1,
          backgroundColor: checkedColors.barBackground,
          borderColor: checkedColors.barBorderColor,
        },
        '& .MuiSwitch-thumb': {
          backgroundColor: checkedColors.knobBackground,
          borderColor: checkedColors.knobBorderColor,
        },
      },
    },
    '& .MuiSwitch-thumb': {
      width: knobWidth,
      height: knobHeight,
      boxSizing: 'border-box',
      backgroundColor: uncheckedColors.knobBackground,
      border: `1px solid ${uncheckedColors.knobBorderColor}`,
    },
    '& .MuiSwitch-track': {
      width: barWidth,
      height: barHeight,
      borderRadius: barBorderRadius,
      opacity: 1,
      backgroundColor: uncheckedColors.barBackground,
      border: `1px solid ${uncheckedColors.barBorderColor}`,
    },
  };
}

export { ATLAS_LENS_COMPONENT_VARS, ATLAS_LENS_TOGGLE_VARS };
